import { defineStore } from 'pinia';
import { ref } from 'vue';
import { UserService } from '@/services/user.service';
import type {
  IAssignmentsResponse,
  IBodyUser,
  IBodyUserCustomer,
  IBodyUserInfo,
  IBodyUserUpdatePassword,
  ICustomersResponse,
  ICustomerUserResponse,
  IRemovalsResponse,
  IRoleBody,
  IStaffResponse,
  IStaffSingleResponse,
  IUpdateStaffRole,
  IUserPaginatedResponse,
} from '@/types/user.type';
import type { IFileImage } from '@/types/file.types';

/**
 * Store pour gérer les  users et leurs actions associées
 */
export const useUserStore = defineStore('user', () => {
  /**
   * Crée un nouveau client via le service distant.
   *
   * Cette fonction envoie les données d'un client au backend pour
   * l'enregistrement dans la base de données.
   *
   * @async
   * @function createUserCustomer
   * @param {IBodyUserCustomer} data - Données du client à créer.
   * @returns {Promise<any>} Réponse de l'API contenant les informations du client créé.
   * @throws {AxiosError} Lance une erreur si la requête échoue ou si le service renvoie une erreur.
   */
  const createUserCustomer = async (data: IBodyUserCustomer): Promise<any> => {
    try {
      const response = await UserService.createUserCustomer(data);
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  /**
   * État réactif contenant la liste des clients récupérés depuis l’API.
   *
   * Ce ref stocke la réponse typée de l’API après l’appel à `getUserCustomer`.
   * Il est mis à jour automatiquement lorsque les données sont chargées
   * depuis le service distant.
   *
   * @type {Ref<ICustomersResponse | null>}
   */
  const datagetUserCustomer = ref<ICustomersResponse | null>(null);

  /**
   * Récupère la liste des clients depuis le service distant.
   *
   * Cette fonction effectue une requête HTTP via le `UserService`
   * pour récupérer les clients disponibles, puis met à jour
   * l’état réactif `datagetUserCustomer` avec les données renvoyées.
   *
   * @async
   * @function getUserCustomer
   * @returns {Promise<ICustomersResponse>} Promesse résolue contenant les données des clients récupérés.
   * @throws {AxiosError} Lance une erreur si la récupération échoue.
   */
  const getUserCustomer = async (): Promise<ICustomersResponse> => {
    const response = await UserService.getUserCustomer();
    datagetUserCustomer.value = response.data;
    return response.data;
  };

  /**
   * État réactif contenant les informations d’un client récupéré depuis l’API.
   *
   * Ce `ref` stocke la réponse typée de l’API après l’appel à `getUserCustomerById`.
   * Il est mis à jour automatiquement lorsque les données sont chargées
   * depuis le service distant.
   *
   * @type {Ref<ICustomerUserResponse | null>}
   */
  const dataGetUserCustomersById = ref<ICustomerUserResponse | null>(null);

  /**
   * Récupère un client spécifique depuis le service distant par son identifiant.
   *
   * Cette fonction effectue une requête HTTP via `UserService`
   * pour obtenir les informations du client, puis met à jour
   * l’état réactif `dataGetUserCustomersById` avec les données reçues.
   *
   * @async
   * @function getUserCustomerById
   * @returns {Promise<ICustomerUserResponse>} Promesse résolue contenant les données du client récupéré.
   * @throws {AxiosError} Lance une erreur si la récupération échoue.
   */
  const getUserCustomerById = async (): Promise<ICustomerUserResponse> => {
    const response = await UserService.getUserCustomerById();
    dataGetUserCustomersById.value = response.data;
    return response.data;
  };

  /**
   * Met à jour un client spécifique sur le service distant.
   *
   * Cette fonction effectue une requête HTTP via `UserService`
   * pour modifier les informations d’un client existant,
   * puis renvoie les données mises à jour.
   *
   * @async
   * @function updateUserCustomerById
   * @param {Partial<IBodyUserCustomer>} userCustomerData - Données partielles du client à mettre à jour.
   * @returns {Promise<any>} Promesse résolue contenant les données mises à jour du client.
   * @throws {AxiosError} Lance une erreur si la mise à jour échoue.
   */
  const updateUserCustomerById = async (
    userCustomerData: Partial<IBodyUserCustomer>,
  ): Promise<any> => {
    const response = await UserService.updateUserCustomer(userCustomerData);
    return response.data;
  };

  /**
   * Crée un nouveau staff role via le service distant.
   *
   * Cette fonction envoie les données d'un staff role au backend pour
   * l'enregistrement dans la base de données.
   *
   * @async
   * @function createUserRoleStaff
   * @param {IRoleBody} data - Données du client à créer.
   * @returns {Promise<any>} Réponse de l'API contenant les informations du client créé.
   * @throws {AxiosError} Lance une erreur si la requête échoue ou si le service renvoie une erreur.
   */
  const createUserRoleStaff = async (data: IRoleBody): Promise<any> => {
    try {
      const response = await UserService.createUserRoleStaff(data);
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
  /**
   * État réactif contenant la liste des roles staff récupérés depuis l’API.
   *
   * Ce ref stocke la réponse typée de l’API après l’appel à `getUserStaffRole`.
   * Il est mis à jour automatiquement lorsque les données sont chargées
   * depuis le service distant.
   *
   * @type {Ref<IStaffResponse | null>}
   */
  const datagetUserStaffRole = ref<IStaffResponse | null>(null);

  /**
   * Récupère la liste des staff roles depuis le service distant.
   *
   * Cette fonction effectue une requête HTTP via le `UserService`
   * pour récupérer les staffs disponibles, puis met à jour
   * l’état réactif `datagetUserStaffRole` avec les données renvoyées.
   *
   * @async
   * @function getUserStaffRole
   * @returns {Promise<IStaffResponse>} Promesse résolue contenant les données des clients récupérés.
   * @throws {AxiosError} Lance une erreur si la récupération échoue.
   */
  const getUserStaffRole = async (): Promise<IStaffResponse> => {
    const response = await UserService.getUserStaffRole();
    datagetUserStaffRole.value = response.data;
    return response.data;
  };

  /**
   * État réactif contenant la liste des roles staff récupérés depuis l’API.
   *
   * Ce ref stocke la réponse typée de l’API après l’appel à `getUserStaffRole`.
   * Il est mis à jour automatiquement lorsque les données sont chargées
   * depuis le service distant.
   *
   * @type {Ref<IStaffSingleResponse | null>}
   */
  const dataGetUserStaffRoleById = ref<IStaffSingleResponse | null>(null);
  /**
   * Récupère un stff role spécifique depuis le service distant par son identifiant.
   *
   * Cette fonction effectue une requête HTTP via `UserService`
   * pour obtenir les informations du staff, puis met à jour
   * l’état réactif `dataGetUserStaffRoleById` avec les données reçues.
   *
   * @async
   * @function  getUserStaffRoleById
   * @returns {Promise<IStaffSingleResponse>} Promesse résolue contenant les données récupéré.
   * @throws {AxiosError} Lance une erreur si la récupération échoue.
   */
  const getUserStaffRoleById = async (): Promise<IStaffSingleResponse> => {
    const response = await UserService.getUserStaffRoleById();
    dataGetUserStaffRoleById.value = response.data;
    return response.data;
  };

  /**
   * Met à jour un role staff spécifique sur le service distant.
   *
   * Cette fonction effectue une requête HTTP via `UserService`
   * pour modifier les informations d’un staff role existant,
   * puis renvoie les données mises à jour.
   *
   * @async
   * @function updateUserStaffRole
   * @param {Partial<IUpdateStaffRole>} userStaffRoleData - Données partielles du client à mettre à jour.
   * @returns {Promise<any>} Promesse résolue contenant les données mises à jour du client.
   * @throws {AxiosError} Lance une erreur si la mise à jour échoue.
   */
  const updateUserStaffRole = async (
    userCustomerData: Partial<IUpdateStaffRole>,
  ): Promise<any> => {
    const response = await UserService.updateUserStaffRole(userCustomerData);
    return response.data;
  };

  /**
   * Supprime un rôle staff spécifique via le service distant.
   *
   * Cette fonction envoie une requête HTTP à l’aide de `UserService`
   * pour retirer un rôle staff existant,
   * puis renvoie la réponse du serveur en cas de succès.
   *
   * @async
   * @function DeleteUserStaffRole
   * @returns {Promise<any>} Promesse résolue contenant la réponse du serveur après suppression.
   * @throws {AxiosError} Lance une erreur si la suppression échoue.
   */
  const DeleteUserStaffRole = async (): Promise<any> => {
    try {
      const response = await UserService.DeleteUserStaffRole();
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
  /**
   * Récupère la liste de toutes les permissions disponibles depuis le service distant.
   *
   * Cette fonction appelle le `UserService.getAllAvailablePermission`
   * afin d’obtenir les permissions configurées dans le système.
   * Elle retourne directement les données extraites de la réponse HTTP,
   * pouvant être utilisées pour l’affichage ou la gestion des droits d’accès.
   *
   * @async
   * @function getAllAvailablePermission
   * @returns {Promise<any>} Promesse résolue contenant la liste des permissions disponibles.
   * @throws {AxiosError} Lance une erreur si la requête au service échoue.
   */
  const getAllAvailablePermission = async (): Promise<any> => {
    const response = await UserService.getAllAvailablePermission();
    return response.data;
  };

  /**
   * Crée un nouveau client via le service distant.
   *
   * Cette fonction envoie les données d'un client au backend pour
   * l'enregistrement dans la base de données. Elle renvoie la réponse
   * complète de l'API contenant les informations du client créé.
   *
   * @async
   * @function createUser
   * @param {IBodyUser} data - Données du client à créer.
   * @returns {Promise<AxiosResponse<any>>}
   *   Promesse résolue avec la réponse de l'API contenant les informations du client créé.
   * @throws {AxiosError} Lance une erreur si la requête HTTP échoue ou si le service renvoie une erreur.
   */
  const createUser = async (data: IBodyUser): Promise<any> => {
    try {
      const response = await UserService.createUser(data);
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
  /**
   * Récupère la liste de tous les utilisateurs depuis le service distant.
   *
   * Cette fonction appelle le `UserService.getUser` afin d’obtenir les
   * utilisateurs configurés dans le système. Elle retourne directement
   * les données extraites de la réponse HTTP, pouvant être utilisées
   * pour l’affichage ou la gestion des utilisateurs.
   *
   * @async
   * @function getUser
   * @returns {Promise<IUserPaginatedResponse>}
   *   Promesse résolue contenant la liste paginée des utilisateurs.
   * @throws {AxiosError} Lance une erreur si la requête au service échoue.
   */
  const getUser = async (): Promise<IUserPaginatedResponse> => {
    const response = await UserService.getUser();
    return response.data;
  };

  /**
   * Récupère les rôles d’un utilisateur (client) spécifique depuis le service distant.
   *
   * Cette fonction effectue une requête HTTP via `UserService.getUserRolesById`
   * pour obtenir les rôles associés à un utilisateur identifié par `userId`.
   * Elle retourne directement les données extraites de la réponse HTTP.
   *
   * @async
   * @function getUserRolesById
   * @returns {Promise<any>}
   *   Promesse résolue contenant les rôles de l’utilisateur récupérés depuis le service.
   * @throws {AxiosError} Lance une erreur si la requête HTTP échoue.
   */
  const getUserRolesById = async (): Promise<any> => {
    const response = await UserService.getUserRolesById();
    return response.data;
  };

  /**
   * Met à jour les informations d’un utilisateur (staff) sur le service distant.
   *
   * Cette fonction effectue une requête HTTP via `UserService.updateMyInfo`
   * pour modifier les informations existantes d’un utilisateur (staff),
   * puis retourne les données mises à jour.
   *
   * @async
   * @function updateMyInfo
   * @param {Partial<IBodyUserInfo>} userCustomerData - Données partielles de l’utilisateur à mettre à jour
   *   (ex. prénom, nom, téléphone, mot de passe, etc.).
   * @returns {Promise<IBodyUserInfo>}
   *   Promesse résolue contenant les informations mises à jour de l’utilisateur.
   * @throws {AxiosError} Lance une erreur si la requête HTTP échoue ou si la mise à jour est refusée par le service.
   */
  const updateMyInfo = async (
    userCustomerData: Partial<IBodyUserInfo>,
  ): Promise<IBodyUserInfo> => {
    const response = await UserService.updateMyInfo(userCustomerData);
    return response.data;
  };

  /**
   * Met à jour les informations d’un utilisateur (staff) par un administrateur sur le service distant.
   *
   * Cette fonction envoie une requête HTTP via `UserService.updateUserInfo`
   * pour modifier les informations existantes d’un utilisateur (staff),
   * telles que prénom, nom, téléphone ou mot de passe.
   * Les données partielles peuvent être fournies pour mettre à jour uniquement certains champs.
   *
   * @async
   * @function updateUserInfo
   * @param {Partial<IBodyUserInfo>} userCustomerData - Données partielles de l’utilisateur à mettre à jour.
   * @returns {Promise<IBodyUserInfo>}
   *   Promesse résolue contenant les informations mises à jour de l’utilisateur.
   * @throws {AxiosError} Lance une erreur si la requête HTTP échoue ou si la mise à jour est refusée par le service.
   */
  const updateUserInfo = async (
    userCustomerData: Partial<IBodyUserInfo>,
  ): Promise<IBodyUserInfo> => {
    const response = await UserService.updateUserInfo(userCustomerData);
    return response.data;
  };

  /**
   * Met à jour le mot de passe d’un utilisateur (staff) par un administrateur sur le service distant.
   *
   * Cette fonction envoie une requête HTTP via `UserService.updateUserPassword`
   * pour modifier uniquement le mot de passe d’un utilisateur existant.
   * Les données partielles peuvent inclure le mot de passe actuel et le nouveau mot de passe.
   *
   * @async
   * @function updateUserPassword
   * @param {Partial<IBodyUserUpdatePassword>} userPasswordData - Données partielles pour la mise à jour du mot de passe.
   * @returns {Promise<IBodyUserInfo>}
   *   Promesse résolue contenant les informations mises à jour de l’utilisateur après le changement de mot de passe.
   * @throws {AxiosError} Lance une erreur si la requête HTTP échoue ou si le service refuse la mise à jour.
   */
  const updateUserPassword = async (
    userPasswordData: Partial<IBodyUserUpdatePassword>,
  ): Promise<IBodyUserInfo> => {
    const response = await UserService.updateUserPassword(userPasswordData);
    return response.data;
  };

  /**
   * Met à jour le mot de passe de l’utilisateur connecté (staff) sur le service distant.
   *
   * Cette fonction envoie une requête HTTP via `UserService.updateMyPassword`
   * pour modifier uniquement le mot de passe de l’utilisateur actuellement connecté.
   * Les données partielles doivent inclure le mot de passe actuel et le nouveau mot de passe.
   *
   * @async
   * @function updateMyPassword
   * @param {Partial<IBodyUserUpdatePassword>} userPasswordData - Données pour la mise à jour du mot de passe.
   * @returns {Promise<IBodyUserInfo>}
   *   Promesse résolue contenant les informations mises à jour de l’utilisateur après le changement de mot de passe.
   * @throws {AxiosError} Lance une erreur si la requête HTTP échoue ou si le service refuse la mise à jour.
   */
  const updateMyPassword = async (
    userPasswordData: Partial<IBodyUserUpdatePassword>,
  ): Promise<IBodyUserInfo> => {
    const response = await UserService.updateMyPassword(userPasswordData);
    return response.data;
  };

  /**
   * Met à jour la photo de profil d’un utilisateur (staff) sur le service distant.
   *
   * Cette fonction envoie une requête HTTP via `UserService.updateUserPhoto`
   * pour modifier la photo de profil de l’utilisateur. Les données partielles
   * doivent inclure le fichier image ou les informations nécessaires pour la mise à jour.
   *
   * @async
   * @function updateUserPhoto
   * @param {Partial<IFileImage>} updateData - Données pour la mise à jour de la photo de l’utilisateur.
   * @returns {Promise<IBodyUserInfo>}
   *   Promesse résolue contenant les informations mises à jour de l’utilisateur,
   *   y compris la nouvelle photo.
   * @throws {AxiosError} Lance une erreur si la requête HTTP échoue ou si le service refuse la mise à jour.
   */
  const updateUserPhoto = async (
    updateData: Partial<IFileImage>,
  ): Promise<IBodyUserInfo> => {
    const response = await UserService.updateUserPhoto(updateData);
    return response.data;
  };

  /**
   * Met à jour la photo de profil de l’utilisateur connecté (staff) sur le service distant.
   *
   * Cette fonction envoie une requête HTTP via `UserService.updateMyPhoto`
   * pour modifier la photo de profil de l’utilisateur actuellement connecté.
   * Les données partielles doivent inclure le fichier image ou les informations nécessaires pour la mise à jour.
   *
   * @async
   * @function updateMyPhoto
   * @param {Partial<IFileImage>} updateData - Données pour la mise à jour de la photo de l’utilisateur connecté.
   * @returns {Promise<IBodyUserInfo>}
   *   Promesse résolue contenant les informations mises à jour de l’utilisateur,
   *   y compris la nouvelle photo de profil.
   * @throws {AxiosError} Lance une erreur si la requête HTTP échoue ou si le service refuse la mise à jour.
   */
  const updateMyPhoto = async (
    updateData: Partial<IFileImage>,
  ): Promise<IBodyUserInfo> => {
    const response = await UserService.updateMyPhoto(updateData);
    return response.data;
  };

  /**
   * Supprime les rôles assignés à un utilisateur existant sur le service distant.
   *
   * Cette fonction envoie une requête HTTP via `UserService.removeUserRoles`
   * pour retirer les rôles spécifiés d’un utilisateur. Les données partielles
   * doivent inclure la liste des rôles à retirer.
   *
   * @async
   * @function removeUserRoles
   * @param {Partial<IRemovalsResponse>} updateData - Données contenant les rôles à retirer.
   * @returns {Promise<IRemovalsResponse>}
   *   Promesse résolue contenant la confirmation de la suppression des rôles.
   * @throws {AxiosError} Lance une erreur si la requête HTTP échoue ou si le service refuse la suppression.
   */
  const removeUserRoles = async (
    updateData: IRemovalsResponse,
  ): Promise<IRemovalsResponse> => {
    const response = await UserService.removeUserRoles(updateData);
    return response.data;
  };

  /**
   * Assigne des rôles à un utilisateur existant sur le service distant.
   *
   * Cette fonction envoie une requête HTTP via `UserService.assignUserRoles`
   * pour ajouter les rôles spécifiés à un utilisateur. Les données partielles
   * doivent inclure la liste des rôles à assigner et éventuellement les restaurants associés.
   *
   * @async
   * @function assignUserRoles
   * @param {Partial<IAssignmentsResponse>} updateData - Données contenant les rôles à assigner.
   * @returns {Promise<IAssignmentsResponse>}
   *   Promesse résolue contenant la confirmation de l’assignation des rôles.
   * @throws {AxiosError} Lance une erreur si la requête HTTP échoue ou si le service refuse l’assignation.
   */
  const assignUserRoles = async (
    updateData: IAssignmentsResponse,
  ): Promise<IAssignmentsResponse> => {
    const response = await UserService.assignUserRoles(updateData);
    return response.data;
  };

  /**
   * Supprime un utilisateur existant sur le service distant.
   *
   * Cette fonction envoie une requête HTTP via `UserService.deleteUser`
   * pour supprimer l'utilisateur correspondant.
   *
   * @async
   * @function deleteUser
   * @returns {Promise<any>}
   *   Promesse résolue lorsque l'utilisateur est supprimé avec succès.
   * @throws {AxiosError} Lance une erreur si la requête HTTP échoue ou si le service refuse la suppression.
   */
  const deleteUser = async (): Promise<any> => {
    const response = await UserService.deleteUser();
    return response.data;
  };

  const assignUserRolesById = async (
    targetUserId: string,
    assignments: { roleId: string; restaurantId: string }[],
  ): Promise<any> => {
    const response = await UserService.assignUserRolesById(
      targetUserId,
      assignments,
    );
    return response.data;
  };

  const updateUserPhotoById = async (
    targetUserId: string,
    file: File,
    designation: string,
    restaurantId: string,
  ): Promise<any> => {
    const response = await UserService.updateUserPhotoById(
      targetUserId,
      file,
      designation,
      restaurantId,
    );
    return response.data;
  };

  const updateUserPasswordById = async (
    targetUserId: string,
    secret: string,
  ): Promise<any> => {
    const response = await UserService.updateUserPasswordById(
      targetUserId,
      secret,
    );
    return response.data;
  };

  const searchRolesGestion = async (): Promise<IStaffResponse> => {
    const response = await UserService.searchRolesGestion();
    return response.data;
  };

  const searchUsersGestion = async (params: {
    role?: string | string[];
    q?: string;
    restaurantId?: string;
    page?: number;
    pageSize?: number;
  }): Promise<IUserPaginatedResponse> => {
    const response = await UserService.searchUsersGestion(params);
    return response.data;
  };

  return {
    createUser,
    createUserCustomer,
    createUserRoleStaff,
    getUser,
    getUserCustomer,
    datagetUserCustomer,
    getUserCustomerById,
    dataGetUserCustomersById,
    getUserRolesById,
    getUserStaffRole,
    datagetUserStaffRole,
    getUserStaffRoleById,
    getAllAvailablePermission,
    updateMyInfo,
    updateUserInfo,
    updateMyPhoto,
    updateUserPhoto,
    updateMyPassword,
    updateUserPassword,
    updateUserCustomerById,
    updateUserStaffRole,
    assignUserRoles,
    removeUserRoles,
    DeleteUserStaffRole,
    deleteUser,
    assignUserRolesById,
    updateUserPhotoById,
    updateUserPasswordById,
    searchRolesGestion,
    searchUsersGestion,
  };
});
